import { Delete, Edit } from '@mui/icons-material'
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from '@mui/material'
import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { useBoolean } from 'hooks/useBoolean'
import { IInfoCenterNewsItem } from 'types/info-center/news'
import { useNavigate } from 'react-router-dom'

interface Props {
  news: IInfoCenterNewsItem[]
  onDelete?: (planId: string) => void
  canEdit?: boolean
}

const StyledTable = styled(Table)(() => ({
  borderCollapse: 'collapse',
  width: '100%',
  '& th, & td': {
    border: '1px solid #dddddd !important',
  },
  '& th': {
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-root': {
    paddingLeft: '12px',
    paddingRight: '12px',
  },
}))

export const NewsTable: FunctionComponent<Props> = ({
  news,
  onDelete,
  canEdit = true,
}) => {
  const { t } = useTranslation()
  const dialogDeleteReport = useBoolean(false)
  const [newsId, setNewsId] = useState('')
  const navigate = useNavigate()

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <ConfirmationDialog
        open={dialogDeleteReport.isTrue}
        onClose={dialogDeleteReport.setFalse}
        onConfirm={() => onDelete!(newsId as string)}
        text={t('are-you-sure-you-want-to-delete')}
      />
      <TableContainer>
        <StyledTable size="small" sx={{ minWidth: 650, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('title')}</TableCell>
              <TableCell>{t('date')}</TableCell>
              {canEdit && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {news.map((newsItem) => (
              <TableRow
                key={newsItem.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell scope="row">{newsItem.title}</TableCell>
                <TableCell scope="row">
                  {formatTimeStampToDate(newsItem.publishedAt)}
                </TableCell>
                {canEdit && (
                  <TableCell sx={{ width: '100px' }}>
                    <IconButton
                      onClick={() => {
                        navigate(`/portal/news/edit/${newsItem.id}`)
                      }}
                    >
                      <Edit color="primary" fontSize="small" />
                    </IconButton>
                    {onDelete && (
                      <IconButton
                        onClick={() => {
                          setNewsId(newsItem.id)
                          dialogDeleteReport.setTrue()
                        }}
                      >
                        <Delete color="error" fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Paper>
  )
}
